import useAssignmentById from '../../../assignment/hooks/use-assignment-by-id/useAssignmentById';
import {
  CommissionType, DescendantsStrategy, InvoiceType, JobTitle,
} from '../../../domain/types';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import { Commission, Invoice, Agent } from '../../../providers/api/dtos';
import useAgency from '../../../agency/hooks/use-agency/useAgency';

const EXTRA_PACKAGE_DEFAULT_COMMISSION_PERCENTAGES = {
  FRANCHISING: 1,
  AGENT: 0.5,
};

export type CommissionSuggestionsDataResult = {
  data?: Commission[];
  isLoading: boolean;
  errors?: Error[];
};

const createCommission = (
  agentId: number,
  amount: number,
  percentage: number,
  type: CommissionType = CommissionType.COMMISSION,
): Commission => ({
  agentId,
  type,
  amount: amount * percentage,
  percentage: percentage * 100,
});

/**
 * Ottiene la percentuale di commissione appropriata in base al tipo di assignment
 */
const getPercentageCommission = (
  commissionSplit?: { personal?: number; dove?: number },
  isPersonal?: boolean,
): number => {
  if (!commissionSplit) return 0;
  return (isPersonal ? commissionSplit.personal : commissionSplit.dove) ?? 0;
};

const getManagementCommissions = (
  agent: Agent,
  invoiceValue: number,
  areaManager?: Agent,
): Commission[] => {
  const suggestions: Commission[] = [];

  if ([JobTitle.TEAM_LEADER, JobTitle.AREA_MANAGER].includes(agent.jobTitle)) return suggestions;

  const teamLeaderSuggestions = areaManager
    ?.canManage
    ?.filter((canManage) => canManage.id === agent.idParent)
    ?.map((teamLeader) => {
      const percentage = teamLeader.commissions?.managementCost ?? 0;
      return createCommission(teamLeader.id!, invoiceValue, percentage, CommissionType.OVER);
    })
    ?? [];

  suggestions.push(...teamLeaderSuggestions);

  if (areaManager) {
    const percentage = areaManager.commissions?.managementCost ?? 0;
    suggestions.push(createCommission(areaManager.id!, invoiceValue, percentage, CommissionType.OVER));
  }

  return suggestions;
};

/**
 * Verifica se una commissione suggerita esiste già nella lista delle commissioni dell'invoice.
 * La corrispondenza si realizza se i campi agentId e type coincidono.
 */
const isCommissionAlreadyExists = (
  suggestion: Commission,
  existingCommissions: Commission[] = [],
): boolean => existingCommissions.some((existing) => existing.agentId === suggestion.agentId && existing.type === suggestion.type);

export default function useCommissionSuggestions(invoice: Invoice): CommissionSuggestionsDataResult {
  const { data: assignment, isLoading: isAssignmentLoading, error: assignmentError } = useAssignmentById(invoice.assignmentId);
  const { data: agent, isLoading: isAgentLoading, error: agentError } = useAgent(assignment?.agentId);
  const { data: areaManager, isLoading: isAreaManagerLoading, error: areaManagerError } = useAgent(!agent?.franchising ? agent?.areaManagerId : undefined, {
    descendantsStrategy: DescendantsStrategy.ALL,
  });
  const { data: agency, isLoading: isAgencyLoading, error: agencyError } = useAgency(agent?.franchising ? agent.agencies?.[0].id : undefined);

  const suggestions: Commission[] = [];

  if (agent?.franchising && agency) {
    if (invoice.type === InvoiceType.MEDIATION) {
      const percentage = getPercentageCommission(agency.commissions?.commissionSplit, assignment?.personal);
      suggestions.push(createCommission(agent.id!, invoice.value, percentage));
    } else if (invoice.type === InvoiceType.EXTRA_PACKAGE) {
      suggestions.push(createCommission(agent.id!, invoice.value, EXTRA_PACKAGE_DEFAULT_COMMISSION_PERCENTAGES.FRANCHISING));
    }
  } else if (agent) {
    if (invoice.type === InvoiceType.MEDIATION) {
      const percentage = getPercentageCommission(agent.commissions?.commissionSplit, assignment?.personal);
      suggestions.push(
        createCommission(agent.id!, invoice.value, percentage),
        ...getManagementCommissions(agent, invoice.value, areaManager),
      );
    } else if (invoice.type === InvoiceType.EXTRA_PACKAGE) {
      suggestions.push(
        createCommission(agent.id!, invoice.value, EXTRA_PACKAGE_DEFAULT_COMMISSION_PERCENTAGES.AGENT),
        ...getManagementCommissions(agent, invoice.value, areaManager),
      );
    }
  }

  const filteredSuggestions = suggestions
    .filter((suggestion) => suggestion.amount > 0)
    .filter((suggestion) => !isCommissionAlreadyExists(suggestion, invoice.commissions));

  const errors = [assignmentError, agentError, areaManagerError, agencyError].filter((item) => item !== undefined);

  return {
    data: filteredSuggestions,
    isLoading: isAssignmentLoading || isAgentLoading || isAreaManagerLoading || isAgencyLoading,
    errors: errors.length > 0 ? errors : undefined,
  };
}
